import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  confirmDialogSignal,
  ConfirmOptions,
} from '@kinet-open-network/client/shared/ui';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { ConfirmDialog, ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';

interface ConfirmDialogWithAcceptButtonProps extends ConfirmDialog {
  acceptButtonProps: any;
}

@Component({
  selector: 'kon-confirm-dialog',
  imports: [
    CommonModule,
    RouterModule,
    ConfirmDialogModule,
    DynamicFormComponent,
  ],
  providers: [ConfirmationService],
  template: `<p-confirmDialog
    [autoZIndex]="true"
    (onHide)="onHide($event)"
    appendTo="body"
  >
    <ng-template let-cfg pTemplate="message">
      <div class="w-full">
        @if (cfg.message) {
          <div class="flex items-center">
            @if (cfg.icon) {
              <i [class]="'mr-2 ' + cfg.icon"></i>
            }
            <div
              class="grow font-semibold pb-0"
              [innerHTML]="cfg.message"
            ></div>
          </div>
        }
        @if (cfg.detail) {
          <div
            [class]="cfg.detailStyleClass ?? 'pt-2'"
            [innerHTML]="cfg.detail"
          ></div>
        }
        @if (cfg.form) {
          @let formCfg = cfg.form;
          <div [class]="cfg.detail || cfg.message ? 'mt-6' : ''">
            <kon-dynamic-form
              #formComponent
              class="w-full"
              [fields]="formCfg.fields"
              [loading]="formCfg.loading"
              [model]="formCfg.model"
              [options]="formCfg.options"
              (modelChange)="onFormModelChange(cfg, $event)"
              [trackDirtyState]="false"
            />
          </div>
        }
      </div>
    </ng-template>
  </p-confirmDialog>`,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  confirmationService = inject(ConfirmationService);
  private formComponent = viewChild(DynamicFormComponent);
  private dialogComponent = viewChild(ConfirmDialog);

  constructor() {
    effect(() => {
      const confirmation = confirmDialogSignal();
      if (confirmation) {
        if (confirmation.form?.onSubmit) {
          confirmation.accept = () => {
            const formComponent = this.formComponent();
            formComponent?.purgeDirtyState();
            const model = formComponent?.model;
            if (model) {
              return confirmation.form?.onSubmit?.(model);
            }
          };
        }
        this.confirmationService.confirm(confirmation);
      }
    });
  }

  onHide(_: ConfirmEventType) {
    confirmDialogSignal.set(undefined);
  }

  onFormModelChange(formCfg: ConfirmOptions, model: any) {
    const dialog = this.dialogComponent() as ConfirmDialogWithAcceptButtonProps;
    if (dialog) {
      const formValid = !!this.formComponent()?.form?.valid;
      const allowInvalidSubmit =
        formCfg.form?.options?.allowInvalidSubmit ?? false;

      if (!allowInvalidSubmit) {
        dialog.acceptButtonProps = {
          ...dialog.acceptButtonProps,
          disabled: !formValid,
        };
      }
    }
    formCfg.form?.onModelChange?.(model);
  }
}
